<template>
  <div class="flex flex-col p-[1rem] w-full">
    <div class="flex w-full">
      <selectable-card
        :items="approvalItems"
        :selected-status="selectedStatus"
        :icon-lookup-function="getApprovalStatusIcon"
        @select-status="setSelectedStatus"
      />
    </div>
    <div class="flex ml-auto w-1/3">
      <my-students-search />
    </div>
    <div class="flex p-[0.2rem] justify-between">
      <div class="flex flex-grow">
        <!-- TODO include this component in my students filter summary and test if its still working-->
        <my-students-refinements />
      </div>
      <div class="flex p-[0.2rem] justify-between">
        <my-students-results-sort />
      </div>
    </div>
    <div>
      <div hidden>
        <!-- ResultsWrapper unmounts the approval list when nbHits === 0, so the list can't emit
             its own zeroed counts. Watch the result count here and reset the status cards on empty. -->
        <ais-stats>
          <template #default="{ nbHits }">{{ syncEmptyResults(nbHits) }}</template>
        </ais-stats>
      </div>
      <results-wrapper>
        <ais-hits>
          <template v-slot="{ items }">
            <my-students-approval-list
              :orders="items"
              :selected-status="selectedStatus"
              @update-application-approvals="setApplicationApprovals"
            />
          </template>
        </ais-hits>
      </results-wrapper>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch } from "vue";
import { AisHits, AisStats } from "vue-instantsearch/vue3/es";
import { useRoute, useRouter } from "vue-router";

import { useTenantEntityManager } from "@/composables/useTenantManager";
import { approvalStatus, getApprovalStatusIcon } from "./approvalStatus";
import ResultsWrapper from "./Display/ResultsWrapper.vue";
import SelectableCard from "./Display/SelectableCard.vue";
import MyStudentsApprovalList from "./MyStudentsApprovalList.vue";
import MyStudentsRefinements from "./MyStudentsRefinements.vue";
import MyStudentsResultsSort from "./MyStudentsResultsSort.vue";
import MyStudentsSearch from "./MyStudentsSearch.vue";

const router = useRouter();
const route = useRoute();

const { getSelectedEntityView } = useTenantEntityManager();

const getApprovalItems = (qtyNonApproved = 0, qtyApproved = 0) => {
  return [
    {
      label: approvalStatus.APPROVE,
      count: qtyNonApproved,
      props: {
        fill: "#FEBD43",
      },
    },
    {
      label: approvalStatus.COMPLETED,
      count: qtyApproved,
      props: {},
    },
    {
      label: approvalStatus.ALL,
      count: qtyApproved + qtyNonApproved,
      props: {},
    },
  ];
};

const approvalItems = ref(getApprovalItems(0, 0));

// Defaults to "Needs Approval" view when the URL has no approval_status (initial visit or
// deep-link without the param). Previously the same default was forced via an onBeforeMount
// router.replace, which raced InstantSearch's initial mount and clobbered deep-linked values.
const selectedStatus = computed(() => {
  return route?.query?.approval_status ?? approvalStatus.APPROVE;
});

const setSelectedStatus = (status) => {
  if (status) {
    router?.replace({
      query: { ...route?.query, approval_status: status, acceptance_status: undefined },
    });
  }
};

const setApplicationApprovals = (newApplicationApprovals) => {
  const qtyApproved = newApplicationApprovals?.approvedApplicationIds?.length ?? 0;
  const qtyNonApproved = newApplicationApprovals?.nonApprovedApplicationIds?.length ?? 0;
  approvalItems.value = getApprovalItems(qtyNonApproved, qtyApproved);
};

// MyStudentsApprovalList owns the real counts, but ResultsWrapper unmounts it when the search
// returns no hits — so it can never emit a zeroed result. Zero the cards here instead. Guarded to
// the non-zero → zero transition so we don't rebuild the array on every stats re-render.
const syncEmptyResults = (nbHits) => {
  if (nbHits === 0 && approvalItems.value.some((item) => item.count > 0)) {
    approvalItems.value = getApprovalItems(0, 0);
  }
};

watch(getSelectedEntityView, () => {
  approvalItems.value = getApprovalItems(0, 0);
});
</script>
